import {ConfigObjectType} from '@wallaby-ui/core';
import * as React from 'react';
import {useCallback, useRef} from 'react';
import {
  Filter,
  FILTER_TYPE_BOOL,
  FILTER_TYPE_DATE_MONTH,
  FILTER_TYPE_DATE_ONLY_RANGE,
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_DATE_MONTH_RANGE,
  FILTER_TYPE_FULLTEXT,
  FILTER_TYPE_HIERARCHY,
  FILTER_TYPE_HIERARCHY_MULTI_SELECT,
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_MULTI_SELECT_ASYNC,
  FILTER_TYPE_NUMBER,
  FILTER_TYPE_NUMBER_RANGE,
  FILTER_TYPE_RADIO,
  FILTER_TYPE_SELECT,
  FILTER_TYPE_TEXT,
  FILTER_TYPE_ORGANIZATION,
  FILTER_TYPE_SELECT_ENUM,
  FILTER_TYPE_MULTI_SELECT_ENUM,
  FILTER_TYPE_RADIO_ENUM,
  FILTER_TYPE_AGENS,
  FILTER_TYPE_MEDICATIONS,
} from 'src/js/lib/filtering';
import {
  FilterNode as FilterNodeModel,
  FilterSchema,
} from 'src/js/model/filters_config';

import styles from './FilterNodeContent.less';
import FilterNodeLabelClickable from './FilterNodeLabelClickable';
import {FilterNodeTypeAgens} from './FilterNodeTypeAgens';
import FilterNodeTypeBool from './FilterNodeTypeBool';
import FilterNodeTypeDateMonth from './FilterNodeTypeDateMonth';
import FilterNodeTypeDateMonthRange from './FilterNodeTypeDateMonthRange';
import {FilterNodeTypeDateOnlyRange} from './FilterNodeTypeDateOnlyRange';
import {FilterNodeTypeDateRangeContainer} from './FilterNodeTypeDateRangeContainer';
import {FilterNodeTypeEnumSelect} from './FilterNodeTypeEnumSelect';
import FilterNodeTypeFulltext from './FilterNodeTypeFulltext';
import FilterNodeTypeHierarchyMultiSelect from './FilterNodeTypeHierarchyMultiSelect';
import {FilterNodeTypeMedications} from './FilterNodeTypeMedications';
import FilterNodeTypeNumber from './FilterNodeTypeNumber';
import {FilterNodeTypeNumberRange} from './FilterNodeTypeNumberRange';
import {FilterNodeTypeOrganization} from './FilterNodeTypeOrganization';
import {FilterNodeTypeRadio} from './FilterNodeTypeRadio';
import {FilterNodeTypeRadioEnum} from './FilterNodeTypeRadioEnum';
import FilterNodeTypeSelectAsync from './FilterNodeTypeSelectAsync';
import {FilterNodeTypeSelectContainer} from './FilterNodeTypeSelectContainer';
import {FilterNodeTypeTextContainer} from './FilterNodeTypeTextContainer';

interface OwnProps {
  autocomplete: (input: any) => void;
  autocompleteSetValues?: (values: any) => void;
  config: ConfigObjectType;
  filterFocused?: boolean;
  hierarchyStateId: string;
  isOpened?: boolean;
  node: FilterNodeModel;
  changeFilter: (values: any) => void;
  isFilterSelected: (filter: Filter) => void;
  removeFilter: (filter: Filter) => void;
  selectedAutocompleteValues?: any[];
  selectedFilter?: Filter;
  schema: FilterSchema;
  toggleOpened: () => void;
  parentName: any;
  isMui?: boolean;
}

type Props = OwnProps;

const FilterNodeContent: React.FC<Props> = (props) => {
  const {
    autocomplete,
    autocompleteSetValues,
    changeFilter,
    config,
    hierarchyStateId,
    isFilterSelected,
    isOpened = false,
    node,
    parentName,
    removeFilter,
    schema,
    selectedAutocompleteValues,
    selectedFilter,
    toggleOpened,
    filterFocused = false,
    isMui,
  } = props;

  const inputElemRef = useRef<HTMLInputElement>();

  const inputRef = useCallback((inputElement: HTMLInputElement) => {
    inputElemRef.current = inputElement;
  }, []);

  const setFocus = useCallback(() => {
    if (isOpened && filterFocused && inputElemRef.current?.focus) {
      inputElemRef.current.focus();
    }
  }, [isOpened, filterFocused]);

  let renderedFilter;
  switch (node.getType()) {
    case FILTER_TYPE_BOOL: {
      renderedFilter = (
        <FilterNodeTypeBool
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          parentName={parentName}
          isFilterSelected={isFilterSelected}
          selectedFilter={selectedFilter}
          schema={schema}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_RADIO: {
      renderedFilter = (
        <FilterNodeTypeRadio
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          schema={schema}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_DATE_ONLY_RANGE: {
      renderedFilter = (
        <FilterNodeTypeDateOnlyRange
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui={isMui}
        />
      );
      break;
    }
    case FILTER_TYPE_DATE_RANGE: {
      renderedFilter = (
        <FilterNodeTypeDateRangeContainer
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui={isMui}
        />
      );
      break;
    }
    case FILTER_TYPE_DATE_MONTH: {
      renderedFilter = (
        <FilterNodeTypeDateMonth
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_DATE_MONTH_RANGE: {
      renderedFilter = (
        <FilterNodeTypeDateMonthRange
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_FULLTEXT: {
      renderedFilter = (
        <FilterNodeTypeFulltext
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_SELECT:
    case FILTER_TYPE_MULTI_SELECT: {
      renderedFilter = (
        <FilterNodeTypeSelectContainer
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          schema={schema}
          multi={node.getType() === FILTER_TYPE_MULTI_SELECT}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_MULTI_SELECT_ASYNC: {
      renderedFilter = (
        <FilterNodeTypeSelectAsync
          autocomplete={autocomplete}
          autocompleteSetValues={autocompleteSetValues}
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedAutocompleteValues={selectedAutocompleteValues}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_HIERARCHY:
    case FILTER_TYPE_HIERARCHY_MULTI_SELECT: {
      renderedFilter = (
        <FilterNodeTypeHierarchyMultiSelect
          autocomplete={autocomplete}
          autocompleteSetValues={autocompleteSetValues}
          config={config}
          hierarchyStateId={hierarchyStateId}
          multi={node.getType() === FILTER_TYPE_HIERARCHY_MULTI_SELECT}
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedAutocompleteValues={selectedAutocompleteValues}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_NUMBER: {
      renderedFilter = (
        <FilterNodeTypeNumber
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_NUMBER_RANGE: {
      renderedFilter = (
        <FilterNodeTypeNumberRange
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_MEDICATIONS: {
      renderedFilter = (
        <FilterNodeTypeMedications
          multi
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui={isMui}
        />
      );
      break;
    }
    case FILTER_TYPE_ORGANIZATION: {
      renderedFilter = (
        <FilterNodeTypeOrganization
          multi
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui={isMui}
          config={config}
        />
      );
      break;
    }
    case FILTER_TYPE_AGENS: {
      renderedFilter = (
        <FilterNodeTypeAgens
          autocomplete={autocomplete}
          autocompleteSetValues={autocompleteSetValues}
          multi
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedAutocompleteValues={selectedAutocompleteValues}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    }
    case FILTER_TYPE_SELECT_ENUM:
    case FILTER_TYPE_MULTI_SELECT_ENUM: {
      renderedFilter = (
        <FilterNodeTypeEnumSelect
          node={node}
          multi={node.getType() === FILTER_TYPE_MULTI_SELECT_ENUM}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          schema={schema}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui={isMui}
        />
      );
      break;
    }
    case FILTER_TYPE_RADIO_ENUM:
      renderedFilter = (
        <FilterNodeTypeRadioEnum
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          schema={schema}
          inputRef={inputRef}
          setFocus={setFocus}
        />
      );
      break;
    case FILTER_TYPE_TEXT: {
      renderedFilter = (
        <FilterNodeTypeTextContainer
          node={node}
          isOpened={isOpened}
          toggleOpened={toggleOpened}
          changeFilter={changeFilter}
          removeFilter={removeFilter}
          selectedFilter={selectedFilter}
          inputRef={inputRef}
          setFocus={setFocus}
          isMui={isMui}
        />
      );
      break;
    }
    default:
      renderedFilter = (
        <FilterNodeLabelClickable
          label={node.getLabel()}
          onClick={toggleOpened}
        />
      );
  }
  return <span className={styles.dlFilterNodeContent}>{renderedFilter}</span>;
};

FilterNodeContent.displayName = 'FilterNodeContent';

export default FilterNodeContent;
